import { useEffect, useRef, useState } from 'react';
import { Play, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import { useT } from '../i18n';
import type { Example } from '../lib/format-text';
import { haptic } from '../lib/tma';

/**
 * The one example of «Формат и текст» (spec 4.1): a reel's demo plays by itself,
 * muted, in a loop, at 9:16 and about half the screen wide; the corner button
 * turns the sound on. A carousel shows its 4:5 cover. The page holds exactly one
 * <video>: a new example replaces it (and mutes it again); nothing else loads.
 * When the phone refuses to play by itself, the poster and ▶ stay.
 */
export function ExamplePlayer({ example, caption }: { example: Example | null; caption: string }) {
  const t = useT();
  const video = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const src = example?.src ?? null;

  useEffect(() => {
    setMuted(true);
    setBlocked(false);
    const el = video.current;
    if (!el) return;
    // The muted property must be set before play() for the phone to allow autoplay.
    el.muted = true;
    el.play().catch(() => setBlocked(true));
  }, [src]);

  useEffect(() => {
    if (video.current) video.current.muted = muted;
  }, [muted]);

  const play = () => {
    haptic('light');
    setBlocked(false);
    video.current?.play().catch(() => setBlocked(true));
  };

  return (
    <figure className="mb-4">
      {example?.kind === 'video' ? (
        <div className="relative w-[55%] mx-auto aspect-[9/16] rounded-2xl overflow-hidden bg-surface-2 shadow-card">
          <video
            key={example.src}
            ref={video}
            src={example.src}
            poster={example.poster ?? undefined}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
          {blocked ? (
            <button
              type="button"
              onClick={play}
              aria-label={t.formatText.play}
              className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-black/55 text-white flex items-center justify-center active:scale-95 transition-transform"
            >
              <Play size={22} weight="fill" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setMuted((m) => !m);
              }}
              aria-label={muted ? t.formatText.soundOn : t.formatText.soundOff}
              className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center active:scale-95 transition-transform"
            >
              {muted ? <SpeakerSlash size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
            </button>
          )}
        </div>
      ) : example?.kind === 'image' ? (
        <img
          key={example.src}
          src={example.src}
          alt=""
          className="block w-[78%] mx-auto aspect-[4/5] rounded-2xl object-cover bg-surface-2 shadow-card"
        />
      ) : (
        <div className="w-[55%] mx-auto aspect-[9/16] rounded-2xl bg-surface-2" />
      )}
      {caption && <figcaption className="text-xs text-hint text-center mt-2 break-words">{caption}</figcaption>}
    </figure>
  );
}
